import React, { useMemo, useState } from 'react';
import toast from 'react-hot-toast';

import { createPaymentRun, markPaymentRunExported } from '../../api_handlers/invoice';
import { money } from './kit';
import {
  buildRun,
  eligibleForRun,
  runBlockers,
  runPayload,
  toggleMember,
  validateRun,
} from './lib/paymentRun';
import { BANK_FORMATS, DEFAULT_FORMAT, bankFile } from './lib/bankFile';

/**
 * A payment run: the approved invoices finance pays together (subsystem 2).
 *
 * Approval is the only route in (handoff 3), so this list is the approved
 * queue and nothing else. The run produces a bank file; the bank moves the
 * money (D8).
 *
 * Two kinds of finding, and they behave differently. A `stop` — nowhere to
 * send the money, or a net of zero — keeps the line out of the file, because
 * writing an unpayable row is worse than leaving it out and saying so. A
 * `warn` goes in, but is named first: a net estimated from `amount` is not a
 * tax computation, and the same account receiving the same amount twice is
 * what a double payment looks like before it happens.
 */

export const RUN_CSS = `
.pr-wrap { display: grid; gap: 14px; }
.pr-card { border: 1px solid var(--line); border-radius: var(--radius); background: var(--surface); padding: 14px 16px; }
.pr-head { display: flex; align-items: flex-start; justify-content: space-between; gap: 12px; flex-wrap: wrap; }
.pr-title { font-size: 12px; font-weight: 800; letter-spacing: .05em; text-transform: uppercase; color: var(--ink-4); margin: 0; }
.pr-sub { font-size: 12.5px; color: var(--ink-4); margin: 3px 0 0; }
.pr-form { display: grid; grid-template-columns: repeat(auto-fit, minmax(160px, 1fr)); gap: 12px; margin-top: 13px; }
.pr-list { list-style: none; margin: 13px 0 0; padding: 0; display: grid; gap: 1px; background: var(--line); border: 1px solid var(--line); border-radius: var(--radius-sm); overflow: hidden; }
.pr-item { display: flex; align-items: center; gap: 11px; background: var(--surface); padding: 10px 12px; }
.pr-item--out { opacity: .55; }
.pr-item__main { flex: 1 1 auto; min-width: 0; }
.pr-item__name { font-size: 13.5px; font-weight: 650; color: var(--ink); margin: 0; overflow-wrap: anywhere; }
.pr-item__meta { font-size: 11.5px; color: var(--ink-4); margin: 2px 0 0; }
.pr-item__net { font-size: 13.5px; font-weight: 750; color: var(--ink); flex: 0 0 auto; }
.pr-totals { display: flex; flex-wrap: wrap; gap: 10px; margin-top: 13px; }
.pr-fig { flex: 1 1 120px; border: 1px solid var(--line); border-radius: var(--radius-sm); padding: 9px 11px; }
.pr-fig__label { font-size: 10.5px; font-weight: 800; letter-spacing: .06em; text-transform: uppercase; color: var(--ink-4); margin: 0 0 3px; }
.pr-fig__value { font-size: 15px; font-weight: 800; color: var(--ink); }
.pr-flags { list-style: none; margin: 13px 0 0; padding: 0; display: grid; gap: 7px; }
.pr-flag { display: flex; align-items: flex-start; gap: 9px; font-size: 12.5px; line-height: 1.5; border-radius: var(--radius-sm); padding: 9px 11px; }
.pr-flag--stop { color: #912018; background: #FEF3F2; border: 1px solid #FECDCA; }
.pr-flag--warn { color: #92400E; background: #FFFBEB; border: 1px solid #FDE68A; }
.pr-flag__dot { flex: 0 0 auto; width: 7px; height: 7px; border-radius: 50%; margin-top: 6px; }
.pr-flag--stop .pr-flag__dot { background: #B42318; }
.pr-flag--warn .pr-flag__dot { background: #B45309; }
.pr-actions { display: flex; flex-wrap: wrap; justify-content: flex-end; gap: 8px; margin-top: 14px; }
.pr-empty { font-size: 13px; color: var(--ink-4); margin: 12px 0 0; }
`;

const today = () => new Date().toISOString().slice(0, 10);

/**
 * Hand the browser a file. Guarded because object URLs are absent in jsdom
 * and in older Safari, and a download failing is not worth an exception.
 */
function download(name, text) {
  try {
    const blob = new Blob([text], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = name;
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
    URL.revokeObjectURL(url);
    return true;
  } catch (e) {
    return false;
  }
}

function Figure({ label, value }) {
  return (
    <div className="pr-fig">
      <p className="pr-fig__label">{label}</p>
      <div className="pr-fig__value iv-num">{value}</div>
    </div>
  );
}

export default function PaymentRun({ invoices, onCreated }) {
  const [date, setDate] = useState(today);
  const [reference, setReference] = useState('');
  const [format, setFormat] = useState(DEFAULT_FORMAT);
  const [removed, setRemoved] = useState([]);
  const [errors, setErrors] = useState({});
  const [busy, setBusy] = useState(false);

  const eligible = useMemo(() => eligibleForRun(invoices), [invoices]);

  const run = useMemo(() => {
    const full = buildRun({ invoices: eligible, date, reference });
    return removed.reduce((carry, id) => toggleMember(carry, eligible, id), full);
  }, [eligible, date, reference, removed]);

  const flags = useMemo(() => runBlockers(run), [run]);
  const file = useMemo(() => bankFile(run, format), [run, format]);

  const inRun = (id) => run.members.some((member) => member.invoice_id === id);

  function toggle(id) {
    setRemoved((current) =>
      current.includes(id) ? current.filter((each) => each !== id) : [...current, id],
    );
  }

  function exportFile() {
    const found = validateRun(run);
    setErrors(found);
    if (Object.keys(found).length > 0) return;

    if (file.count === 0) {
      toast.error('Nothing in this run can be paid yet.');
      return;
    }

    if (!download(file.name, file.csv)) {
      toast.error('Could not start the download in this browser.');
      return;
    }
    toast.success(`${file.count} payment${file.count === 1 ? '' : 's'} written to ${file.name}`);
  }

  async function create() {
    const found = validateRun(run);
    setErrors(found);
    if (Object.keys(found).length > 0) return;

    setBusy(true);
    try {
      const created = await createPaymentRun(runPayload(run));
      // Recording the export is what lets the run ask for its reference later.
      // A failure here must not lose the run that was just created.
      if (created && created.id) {
        try {
          await markPaymentRunExported(created.id, { format });
        } catch (e) {
          /* the run exists; the export flag is a convenience */
        }
      }
      toast.success('Payment run created');
      setRemoved([]);
      if (onCreated) onCreated(created);
    } catch (error) {
      if (error.status === 404) {
        // The endpoint went live on prod on 27 Oct (API #2967 -> #2969), so a
        // 404 is now a fault to report rather than a feature to wait for.
        toast.error('The server did not recognise the payment run address. Report this.');
      } else {
        toast.error(error.message || 'Could not create the payment run');
      }
    } finally {
      setBusy(false);
    }
  }

  if (eligible.length === 0) {
    return (
      <section className="pr-card" aria-label="Payment run">
        <div className="pr-head">
          <div>
            <p className="pr-title">Payment run</p>
            <p className="pr-sub">Approved invoices appear here, ready to be paid together.</p>
          </div>
        </div>
        <p className="pr-empty">Nothing is approved and waiting for payment.</p>
      </section>
    );
  }

  return (
    <div className="pr-wrap">
      <section className="pr-card" aria-label="Payment run">
        <div className="pr-head">
          <div>
            <p className="pr-title">Payment run</p>
            <p className="pr-sub">
              {run.totals.count} of {eligible.length} approved invoice
              {eligible.length === 1 ? '' : 's'} in this run.
            </p>
          </div>
        </div>

        <div className="pr-form">
          <label className="iv-field">
            <span className="iv-label">Payment date</span>
            <input
              className="iv-input"
              type="date"
              value={date}
              onChange={(event) => setDate(event.target.value)}
            />
            {errors.date ? <span className="iv-error" role="alert">{errors.date}</span> : null}
          </label>
          <label className="iv-field">
            <span className="iv-label">Reference</span>
            <input
              className="iv-input"
              placeholder="OCT-1"
              value={reference}
              onChange={(event) => setReference(event.target.value)}
            />
          </label>
          <label className="iv-field">
            <span className="iv-label">Bank format</span>
            <select
              className="iv-input"
              value={format}
              onChange={(event) => setFormat(event.target.value)}
            >
              {BANK_FORMATS.map((each) => (
                <option key={each.id} value={each.id}>
                  {each.label}
                </option>
              ))}
            </select>
          </label>
        </div>

        <ul className="pr-list">
          {eligible.map((invoice) => {
            const member = run.members.find((each) => each.invoice_id === invoice.id);
            const included = inRun(invoice.id);
            return (
              <li key={invoice.id} className={`pr-item${included ? '' : ' pr-item--out'}`}>
                <input
                  type="checkbox"
                  checked={included}
                  onChange={() => toggle(invoice.id)}
                  aria-label={`Include ${invoice.influencer_name || invoice.vendor_name || `invoice ${invoice.id}`}`}
                />
                <div className="pr-item__main">
                  <p className="pr-item__name">
                    {invoice.vendor_name || invoice.influencer_name || `Invoice ${invoice.id}`}
                  </p>
                  <p className="pr-item__meta">
                    {[invoice.campaign_name, invoice.invoice_number].filter(Boolean).join(' · ')}
                  </p>
                </div>
                <span className="pr-item__net iv-num">
                  {member ? money(member.net) : money(invoice.amount)}
                </span>
              </li>
            );
          })}
        </ul>
        {errors.members ? <span className="iv-error" role="alert">{errors.members}</span> : null}

        <div className="pr-totals">
          <Figure label="Taxable" value={money(run.totals.taxable)} />
          <Figure label="GST" value={money(run.totals.gst)} />
          <Figure label="TDS" value={money(run.totals.tds)} />
          <Figure label="Net payable" value={money(run.totals.net)} />
        </div>

        {flags.length > 0 ? (
          <ul className="pr-flags">
            {flags.map((flag) => (
              <li
                key={`${flag.code}-${flag.invoice_id}`}
                className={`pr-flag pr-flag--${flag.severity}`}
              >
                <span className="pr-flag__dot" aria-hidden="true" />
                <span>
                  <strong>{flag.name}</strong> — {flag.message}
                  {flag.severity === 'stop' ? ' This line stays out of the file.' : ''}
                </span>
              </li>
            ))}
          </ul>
        ) : null}

        {file.skipped.length > 0 ? (
          <p className="pr-empty">
            {file.count} line{file.count === 1 ? '' : 's'} will be written; {file.skipped.length}{' '}
            left out.
          </p>
        ) : null}

        <div className="pr-actions">
          <button type="button" className="iv-btn iv-btn--ghost" onClick={exportFile}>
            Download bank file
          </button>
          <button type="button" className="iv-btn iv-btn--primary" onClick={create} disabled={busy}>
            {busy ? <span className="iv-spin" /> : null}
            Create run
          </button>
        </div>
      </section>
    </div>
  );
}
